import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import cx from 'classnames';
import { getLinkFromCasinoGameData } from 'helpers/casino';

import { CasinoApi, get, Logger, useApplicationState, useSettings } from '@atlas/core';
import { Button, EmptyStateView, Loader } from '@atlas/ui';

import { useLocalBetslip } from 'common/Betslip/LocalBetslipContext';
import { defaultRoutes } from 'components/MainRouter/constants';
import SeoPageText from 'components/SeoPage/SeoPageText';
import useSignInRedirects from 'src/components/Auth/useSignInRedirects';

const VirtualGameItem = () => {
  const applicationState = useApplicationState();
  const authenticated = get(applicationState, 'authenticated');
  const { push: historyPush } = useHistory();
  const { pathname } = useLocation();
  const { redirectToSignIn } = useSignInRedirects();

  let gameName = '';
  const parts = pathname.split('/');
  if (parts.length >= 3) {
    gameName = parts[2];
  }

  const { quickBet: isQuickBet } = useSettings();
  const { betIdToEdit } = useLocalBetslip();

  const [gameDetailsData, setGameDetailsData] = useState({});
  const [virtualGameData, setVirtualGameData] = useState({});
  const gameIFrame = useRef();
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  const fetchGameDetails = useCallback(async groupName => {
    try {
      setIsLoading(true);
      const gamesList = await CasinoApi.fetchVirtualGamesList();
      const res = await CasinoApi.fetchVirtualGameDetailsV2(groupName);
      setGameDetailsData(res.data);
      setVirtualGameData(
        gamesList.find(
          game => !!groupName && game.name.toLowerCase() === groupName.toLocaleLowerCase(),
        ),
      );
    } catch (error) {
      setError(JSON.stringify(error, ' ', 2));
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!gameName) {
      historyPush(defaultRoutes.casino);
      return;
    }

    if (authenticated) {
      fetchGameDetails(gameName);
    } else {
      redirectToSignIn({ redirectPath: pathname });
    }
  }, [fetchGameDetails, historyPush, gameName, pathname, authenticated, redirectToSignIn]);

  const src = useMemo(() => {
    if (!authenticated || !gameName) {
      return null;
    }

    return getLinkFromCasinoGameData(gameDetailsData);
  }, [authenticated, gameName, gameDetailsData]);

  useEffect(() => {
    if (authenticated && gameName) {
      const handleIFrameMessage = message => {
        try {
          if (typeof message.data === 'string') {
            const parsedData = JSON.parse(message.data);

            if (parsedData.type === 'iframeTop') {
              gameIFrame.current.contentWindow.scrollTo(0, parsedData.data.topPlius);
            }

            if (parsedData.type === 'new_resize') {
              const iframeElement =
                gameIFrame.current.contentDocument.getElementById('betgames_iframe_1');

              if (iframeElement) {
                iframeElement.height = `${parsedData.data.height}px`;
              }
            }
          }
        } catch (error) {
          Logger.error('Unable to handle iframe action', error);
        }
      };
      window.addEventListener('message', handleIFrameMessage);

      return () => {
        window.removeEventListener('message', handleIFrameMessage);
      };
    }
  }, [authenticated, gameName]);

  const redirectToCasino = () => {
    historyPush(defaultRoutes.casino);
  };

  const layoutToScrollClass = cx('layout-to-scroll', { quickbet: isQuickBet || betIdToEdit });

  if (isLoading) {
    return (
      <div className={layoutToScrollClass}>
        <div className="loader-container align-center">
          <Loader className="absolute" />
        </div>
      </div>
    );
  } else if (!virtualGameData || virtualGameData.tabType !== 'VIRTUAL_GAME_PAGE') {
    redirectToCasino();
  }

  if (error) {
    return (
      <div className={layoutToScrollClass}>
        <EmptyStateView
          className="viewport-height fullscreen"
          description="page_temporarily_unavailable_description"
          title="page_temporarily_unavailable_title"
        >
          <Button
            className="block"
            label="refresh"
            onClick={() => fetchGameDetails(gameName)}
            type="primary"
          />
        </EmptyStateView>
      </div>
    );
  }

  return (
    <div className={layoutToScrollClass}>
      <div className="tv-games-container virtual-games-container">
        <iframe ref={gameIFrame} src={src} />
      </div>
      <SeoPageText />
    </div>
  );
};

export default VirtualGameItem;
